import { ErrorModal, useContextMenuMESF } from "@dexteel/mesf-core";
import Grid2 from "@mui/material/Grid2";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import {
	DefaultMenuItem,
	GetContextMenuItemsParams,
	GridApi,
	MenuItemDef,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useState } from "react";

import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";
import { FooterButton } from "@/controls/buttons/FooterButton";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { SearchTextField } from "@/controls/SearchTextField";
import { CatalogKeeper } from "@/shared/models/CatalogKeeper";
import { useCatalogKeepers } from "../CatalogKeepersPage";
import { useTableData } from "../hooks/useTableData";
import { UpsertDeleteModal } from "./UpsertDeleteModal";

export const TableKeepers = () => {
	const [showModal, setShowModal] = useState<
		"create" | "update" | "delete" | ""
	>("");
	const [selectedRowData, setSelectedRowData] = useState<CatalogKeeper | null>(
		null,
	);
	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const [error, setError] = useState("");
	const [quickFilterText, setQuickFilterText] = useState("");

	const { data: rows, isLoading } = useCatalogKeepers({
		onError: (error) => setError(error.message),
	});

	const { showContextMenu, registerConfig } = useContextMenuMESF();
	const { columnDefs, getMenuOptions } = useTableData({
		setSelectedRowData,
		setShowModal,
		showContextMenu,
	});

	const { defaultColDef } = useGridDefinitions({});

	const onRowDoubleClicked = (event: any) => {
		setSelectedRowData(event.data);
		setShowModal("update");
	};

	const getContextMenuItems = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			const data = params.node?.data;
			showContextMenu(event as any, data, "keepersContext");
			if (data) {
				params.api.deselectAll();
				params.node?.setSelected(true);
			}
			return [];
		},
		[rows],
	);

	useEffect(() => {
		registerConfig({
			id: "keepersContext",
			getOptions: getMenuOptions,
		});
	}, []);

	return (
		<>
			<Grid2 container justifyContent="center" p={1} spacing={1}>
				<Grid2
					container
					size={{ md: 12, xs: 12 }}
					spacing={1}
					direction={"column"}
				>
					<Grid2>
						<Typography variant="h5" fontWeight={600}>
							Keepers
						</Typography>
					</Grid2>
				</Grid2>
				<Grid2 container justifyContent="center" alignItems="center">
					<Grid2 size={{ md: 12, xs: 12 }} style={{ marginBottom: 10 }}>
						<SearchTextField
							value={quickFilterText}
							onChange={(text) => setQuickFilterText(text)}
							label="Search keepers"
						/>
					</Grid2>
					<Grid2 size={{ md: 12, xs: 12 }}>
						<Paper elevation={2} style={{ height: "68vh", width: "100%" }}>
							<AgGridReact<CatalogKeeper>
								loading={isLoading}
								rowData={rows || []}
								columnDefs={columnDefs}
								defaultColDef={defaultColDef}
								rowHeight={34}
								headerHeight={30}
								animateRows
								getContextMenuItems={getContextMenuItems}
								onRowDoubleClicked={onRowDoubleClicked}
								onGridReady={(params) => setGridApi(params.api)}
								loadingOverlayComponent={CenteredLazyLoading}
								getRowId={(params) => `${params.data.KeeperId}`}
								quickFilterText={quickFilterText}
								gridOptions={{
									rowSelection: {
										mode: "singleRow",
										checkboxes: false,
										enableClickSelection: true,
									},
								}}
							/>
						</Paper>
					</Grid2>
					<FooterButton
						label="KEEPER"
						showCreateModal={showModal === "create"}
						setShowCreateModal={(showCreateModal) =>
							showCreateModal ? setShowModal("create") : setShowModal("")
						}
					/>
				</Grid2>
			</Grid2>
			<ErrorModal error={error} onHide={() => setError("")} />
			<UpsertDeleteModal
				keeper={selectedRowData}
				modal={showModal}
				onHide={() => {
					setShowModal("");
				}}
			/>
		</>
	);
};
